/**
 * Everything the PSD2 sub-flow can be showing, as one flat list.
 *
 * This used to be two numbers that had to agree with each other: `insideStep`
 * said which of three screens, and `psd2status` said which outcome once you
 * reached the last one. Nothing stopped them disagreeing, and several places
 * managed it - a failed status paired with the intro step renders nothing at
 * all. One id cannot contradict itself.
 *
 * The order these are declared in does not matter; the order in `PSD2_STATES`
 * does, because that is what the forward and backward walks read.
 */
export const PSD2_STATE = Object.freeze({
    LOADING: "LOADING",
    INTRO: "INTRO",
    BANK_SELECT: "BANK_SELECT",
    IN_PROGRESS: "IN_PROGRESS",
    FAILED: "FAILED",
    RESULT: "RESULT",
    ACCOUNT_MISMATCH: "ACCOUNT_MISMATCH",
    LIMIT_REACHED: "LIMIT_REACHED",
});

/**
 * What a state's "previous" affordance means. Whether it is actually offered
 * is the navigator's call: only it knows what surrounds these screens.
 */
export const PREVIOUS = Object.freeze({
    /** Nowhere to go back to from here. */
    NONE: "NONE",
    /** Back to the previous linear state, inside PSD2. */
    STEP: "STEP",
    /** Back out of PSD2 entirely, into the host flow. */
    LEAVE: "LEAVE",
});

/** What a state's `next` may return instead of naming a state to go to. */
export const GO = Object.freeze({
    /** The following linear state. */
    NEXT: "GO_NEXT",
    /** Hand the host flow forward; we are done with PSD2. */
    LEAVE: "GO_LEAVE",
    /** Nothing to do - typically because the browser is navigating away. */
    STAY: "GO_STAY",
});

/** Sync statuses as reported by the sync service. */
export const SYNC_STATUS = Object.freeze({
    SUCCESS: "SUCCESS",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    LITERALLY_IN_PROGRESS: "LITERALLY_IN_PROGRESS",
    FRAUD: "FRAUD",
    BANK_ACCOUNT_MISMATCH: "BANK_ACCOUNT_MISMATCH",
});

const IN_PROGRESS_SYNC_STATUSES = [
    SYNC_STATUS.IN_PROGRESS,
    SYNC_STATUS.LITERALLY_IN_PROGRESS,
];

export function isSyncInProgress(lastSyncStatus) {
    return IN_PROGRESS_SYNC_STATUSES.includes(lastSyncStatus);
}

/** GoCardless requisition states from which a sync can no longer be resumed. */
export const UNRESUMABLE_REQUISITION_STATUSES = Object.freeze([
    "LN", // LINKED
    "EX", // EXPIRED
    "RJ", // REJECTED
]);
